import { Suspense } from 'react';
import _ from 'lodash';
import {
    Icon,
    SuspenseBoundary,
    CreditDetailsHeader,
    CreditInfo,
    DisputeButton,
    FileList,
    LoaderOverlay
} from 'components';
import { 
    useDeductionDetails,
    usePaymentDocuments, 
    useRefreshDeductionDetails,
} from 'state';
import {
    PaymentDetail,
    CreditDetailHeaderData,
    CreditInfoData,
} from 'types';
import { faMoneyBillTransfer } from '@fortawesome/pro-regular-svg-icons'
import { useBackupDocument, useInvoiceParams } from 'hooks';
import { getPaymentTypeDescription } from 'functions';
import DocumentErrorDialog from 'hooks/DocumentErrorDialog';
import styles from './CreditDetailsPage.module.scss';

type CreditDetailsProps = {
    creditDetails: PaymentDetail;
};

export default function CreditDetailsPage() {
    const { accountId, vendorId, invoiceId, voucherId, adjustmentIdentityKey } = useInvoiceParams();
    const creditDetails = useDeductionDetails(accountId, vendorId, invoiceId, undefined, voucherId, adjustmentIdentityKey);
    const refresh = useRefreshDeductionDetails(accountId, vendorId, invoiceId, undefined, voucherId, adjustmentIdentityKey);
   
    return (
        <SuspenseBoundary
            errorMessage={`Error retrieving Credit details ${invoiceId}`}
            onReset={refresh}
        >
            <CreditDetailsComponent
                creditDetails={creditDetails}
            />
        </SuspenseBoundary>
    );
}

const CreditDetailsComponent = ({ creditDetails }: CreditDetailsProps) => {
    const { vendorId } = useInvoiceParams();

    const getHeaderData = (): CreditDetailHeaderData => {
        return {
            type: getPaymentTypeDescription(creditDetails),
            invoiceDate: creditDetails.invoiceDate ? new Date(creditDetails.invoiceDate).toLocaleDateString() : "",
            paymentIdRef: creditDetails.paymentId ?? 'Not Found',
            creditNumber: creditDetails.invoiceId ?? 'Not Found',
            creditAmount: creditDetails.adjustmentAmount,
            vendorId: vendorId,
            vendorName: '',
        };
    }
    const creditDetailHeader: CreditDetailHeaderData = getHeaderData();

    const getCreditInfo = (): CreditInfoData => {
        return {
            creditNumber: creditDetails.invoiceId ?? 'Not Found',
            creditAmount: creditDetails.adjustmentAmount,
            voucherId: creditDetails.voucherId ?? 'Not Found',
        }
    };
    const creditInfo: CreditInfoData = getCreditInfo();

    return (
        <div className="details-page">
            <div className='details-page-title'>
                <Icon icon={faMoneyBillTransfer}/> 
                Credit Details
            </div>
            <div>
                <div className={styles.buttonContainer}>
                    <DisputeButton 
                        selectedDetails={[creditDetails]} 
                        context="creditDetails"
                    />
                </div>
            </div>
            <div className="details-page-main">
                <div className={styles.header}>
                    <CreditDetailsHeader CreditDetailHeader={creditDetailHeader}/>
                </div>
                <div className={styles.mainContent}>
                    <div className={styles.creditInfoContainer}>
                        <CreditInfo creditInfo={creditInfo}/>
                    </div>
                    <div className={styles.backupDocumentationContainer}>
                        <div className={styles.backupDocumentationTitle}>Backup Documentation</div>
                        <div className={styles.backupDocumentationFiles}>
                            <Suspense fallback={<LoaderOverlay isLoading />}>
                                {<Documents detail={creditDetails} />}
                            </Suspense>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

type DetailProps = {
    detail: PaymentDetail
};

const Documents = ({ detail }: DetailProps) => {
    const documents = usePaymentDocuments(detail);
    const getDocument = useBackupDocument();
    
    return _.isEmpty(documents)
        ? <span className={styles.none}>No documents found</span>
        : <>
            <FileList documents={documents} onClick={getDocument} />
            {getDocument.showError && <DocumentErrorDialog onClose={() => getDocument.setShowError(false)} documentIdentifier={getDocument.errorIdentifier} />}
          </>;
};
